import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { useTelemetryViewEvent } from 'common/hooks';
import {
  campaignDetailsView,
  campaignsInteractionClickCampaignTable,
  campaignsInteractionClickNewCampaign,
  campaignsLimitReachedOverlay,
  campaignsLimitWarningOverlay,
  campaignsView,
  chooseCampaignInteractionClickCampaignSelection,
  chooseCampaignView,
  newCampaignDataCreateCampaign,
  newCampaignInteractionClickCampaignRules,
  newCampaignInteractionClickCampaignRulesBasket,
  newCampaignRulesInteractionClickUserRules,
  newCampaignRulesView,
  newCampaignView,
  updateCampaignDataEndCampaign,
  updateCampaignDataUpdateCampaign,
  updateCampaignInteractionClickCampaignRulesBasket,
  updateCampaignInteractionClickCampaignUpdate,
  updateCampaignRulesInteractionClickUserRules,
  updateCampaignView,
} from 'core/telemetry/avo/Avo';

import {
  CampaignsTargetContent,
  CampaignTypeToAvoCampaignTypeMap,
  campaignTypeToAvoOptionSelectedMap,
  columnIdToTargetContentMap,
} from './constants';
import { CampaignColumnIds } from '../../components/CampaignTable/types';

type UserRules = 'first_time_shoppers' | 'everybody';

export const useTelemetryCampaignsView = () => {
  useTelemetryViewEvent('campaignsView', {}, (durationMs, context) => campaignsView({ durationMs, ...context }));
};

export const telemetryInteractionClickCampaignTable = (columnId: CampaignColumnIds) => {
  const targetContentCampaignsTable = columnIdToTargetContentMap[columnId];
  if (!targetContentCampaignsTable) {
    return;
  }

  campaignsInteractionClickCampaignTable({ targetContentCampaignsTable });
};

export const telemetryChooseCampaignInteractionClickCampaignSelection = (campaignType: CampaignType) => {
  chooseCampaignInteractionClickCampaignSelection({
    optionSelected: campaignTypeToAvoOptionSelectedMap[campaignType],
  });
};

export const telemetryInteractionClickNewCampaign = () => {
  campaignsInteractionClickNewCampaign();
};

export const telemetryNewCampaignDataCreateCampaign = (props: {
  campaignType: CampaignType;
  campaignDuration: number;
}) => {
  newCampaignDataCreateCampaign({
    campaignType: CampaignTypeToAvoCampaignTypeMap[props.campaignType],
    campaignDuration: props.campaignDuration,
  });
};

export const telemetryNewCampaignInteractionClickCampaignRules = (
  targetContent: (typeof CampaignsTargetContent.NEW)[keyof typeof CampaignsTargetContent.NEW],
  campaignType: CampaignType,
) =>
  newCampaignInteractionClickCampaignRules({
    targetContentNewCampaigns: targetContent,
    campaignType: CampaignTypeToAvoCampaignTypeMap[campaignType],
  });

export const useTelemetryNewCampaignRulesView = (campaignType: CampaignType, isEnabled: boolean) => {
  useTelemetryViewEvent(
    `newCampaignRulesView/${campaignType}/${isEnabled}`,
    { campaignType: CampaignTypeToAvoCampaignTypeMap[campaignType] },
    (durationMs, context) => isEnabled && newCampaignRulesView({ durationMs, ...context }),
  );
};

export const telemetryNewCampaignInteractionClickCampaignRulesBasket = (
  minBasketSize: string,
  campaignType: CampaignType,
) => {
  newCampaignInteractionClickCampaignRulesBasket({
    campaignRulesBasketContent: minBasketSize,
    campaignType: CampaignTypeToAvoCampaignTypeMap[campaignType],
  });
};

export const telemetryNewCampaignRulesInteractionClickUserRules = (
  userRules: UserRules,
  campaignType: CampaignType,
) => {
  newCampaignRulesInteractionClickUserRules({
    userRulesContent: userRules,
    campaignType: CampaignTypeToAvoCampaignTypeMap[campaignType],
  });
};

export const telemetryUpdateCampaignDataEndCampaign = (campaignType: CampaignType) => {
  updateCampaignDataEndCampaign({
    campaignType: CampaignTypeToAvoCampaignTypeMap[campaignType],
  });
};

export const telemetryUpdateCampaignDataUpdateCampaign = (props: { campaignType: CampaignType }) => {
  updateCampaignDataUpdateCampaign({
    campaignType: CampaignTypeToAvoCampaignTypeMap[props.campaignType],
  });
};

export const telemetryUpdateCampaignInteractionClickCampaignUpdate = (
  targetContent: (typeof CampaignsTargetContent.EDIT)[keyof typeof CampaignsTargetContent.EDIT],
  campaignType: CampaignType,
) => {
  updateCampaignInteractionClickCampaignUpdate({
    targetContentCampaignEdit: targetContent,
    campaignType: CampaignTypeToAvoCampaignTypeMap[campaignType],
  });
};

export const telemetryUpdateCampaignRulesInteractionClickUserRules = (
  userRules: UserRules,
  campaignType: CampaignType,
) => {
  updateCampaignRulesInteractionClickUserRules({
    userRulesContent: userRules,
    campaignType: CampaignTypeToAvoCampaignTypeMap[campaignType],
  });
};

export const telemetryUpdateCampaignInteractionClickCampaignRulesBasket = (
  minBasketSize: string,
  campaignType: CampaignType,
) => {
  updateCampaignInteractionClickCampaignRulesBasket({
    campaignRulesBasketContent: minBasketSize,
    campaignType: CampaignTypeToAvoCampaignTypeMap[campaignType],
  });
};

export const useTelemetryChooseCampaignView = () => {
  useTelemetryViewEvent('chooseCampaignView', {}, (durationMs, context) =>
    chooseCampaignView({ durationMs, ...context }),
  );
};

export const useTelemetryNewCampaignView = (campaignType?: CampaignType, isEnabled?: boolean) => {
  useTelemetryViewEvent(
    `newCampaignView/${campaignType}/${isEnabled}`,
    { campaignType: CampaignTypeToAvoCampaignTypeMap[campaignType as CampaignType] },
    (durationMs, context) => isEnabled && campaignType && newCampaignView({ durationMs, ...context }),
  );
};

export const useTelemetryUpdateCampaignView = (campaignType?: CampaignType, isEnabled?: boolean) => {
  useTelemetryViewEvent(
    `updateCampaignView/${campaignType}/${isEnabled}`,
    { campaignType: CampaignTypeToAvoCampaignTypeMap[campaignType as CampaignType] },
    (durationMs, context) => isEnabled && campaignType && updateCampaignView({ durationMs, ...context }),
  );
};

export const useTelemetryCampaignDetailsView = (campaignType?: CampaignType) => {
  useTelemetryViewEvent(
    `campaignDetailsView/${campaignType}`,
    { campaignType: CampaignTypeToAvoCampaignTypeMap[campaignType as CampaignType] },
    (durationMs, context) => campaignType && campaignDetailsView({ durationMs, ...context }),
  );
};

export const telemetryCampaignsLimitReachedOverlay = () => {
  campaignsLimitReachedOverlay();
};

export const telemetryCampaignsLimitWarningOverlay = () => {
  campaignsLimitWarningOverlay();
};
